'use client';

import React, { isValidElement } from 'react';
import { type ReactNode, type Ref, createContext, useContext } from 'react';
import { HelpCircle } from '@untitledui/icons';
import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components';
import { HintText } from '@milanote/roma/components/base/input/hint-text';
import { Label } from '@milanote/roma/components/base/input/label';
import { Tooltip, TooltipTrigger } from '@milanote/roma/components/base/tooltip/tooltip';
import { cx, sortCx } from '@milanote/roma/utils/cx';
import { isReactComponent } from '@milanote/roma/utils/is-react-component';

const SEMANTIC_NAME_BASE = 'RomaInputBase';
const SEMANTIC_NAME_TEXT_FIELD = 'RomaTextField';
const SEMANTIC_NAME = 'RomaInput';

const TextFieldContext = createContext<TextFieldProps>({});

export interface InputBaseProps extends TextFieldProps {
    /**
     * Input size.
     * @default "sm"
     */
    size?: 'sm' | 'md';
    /** Placeholder text. */
    placeholder?: string;
    /** Class name for the input. */
    inputClassName?: string;
    /** Class name for the input wrapper. */
    wrapperClassName?: string;
    /** Class name for the tooltip. */
    tooltipClassName?: string;
    groupRef?: Ref<HTMLDivElement>;
    /** Icon component or element to display on the left side of the input. */
    iconLeading?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Icon component or element to display on the right side of the input.
     * Note: Takes precedence over `tooltip` when both are provided.
     */
    iconTrailing?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Tooltip message on hover.
     * Note: Will not be displayed if `iconTrailing` is provided, as `iconTrailing` takes precedence.
     */
    tooltip?: string;
}

const styles = sortCx({
    sizes: {
        sm: {
            input: 'px-3 py-1.5 text-sm',
            input_withTrailingIcon: 'pr-9',
            input_withLeadingIcon: 'pl-10',
            iconLeading: 'left-3',
            iconTrailing: 'right-3',
        },
        md: {
            input: 'px-3.5 py-2.5 text-md',
            input_withTrailingIcon: 'pr-9.5',
            input_withLeadingIcon: 'pl-10.5',
            iconLeading: 'left-3.5',
            iconTrailing: 'right-3.5',
        },
    },
});

export const InputBase = React.forwardRef<HTMLInputElement, Omit<InputBaseProps, 'label' | 'hint'>>(
    (
        {
            tooltip,
            groupRef,
            size = 'sm',
            isInvalid,
            isDisabled,
            iconLeading: LeadingIcon,
            iconTrailing: TrailingIcon,
            placeholder,
            wrapperClassName,
            tooltipClassName,
            inputClassName,
            // Omit this prop to avoid invalid HTML attribute warning
            isRequired: _isRequired,
            ...inputProps
        },
        ref,
    ) => {
        // Check if the input has trailing or leading icons
        const hasTrailingIcon = !!TrailingIcon || !!tooltip;
        const hasLeadingIcon = !!LeadingIcon;

        // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
        const context = useContext(TextFieldContext);

        const inputSize = context?.size || size;

        // Ring styles
        const inputRingStyles = cx(
            // Base ring styles
            'ring-1 ring-inset ring-primary',

            // Disabled state styles
            isDisabled && 'ring-disabled',
            'group-disabled:ring-disabled',

            // Invalid state styles
            isInvalid && 'ring-error_subtle',

            // Focus styles
            'focus:ring-2 focus:ring-primary_focused',

            // Invalid state with focus styles
            isInvalid && 'focus:ring-2 focus:ring-error',
        );

        return (
            <AriaGroup
                {...{ isDisabled, isInvalid }}
                ref={groupRef}
                className={({ isFocusWithin, isDisabled, isInvalid }) =>
                    cx(
                        SEMANTIC_NAME_BASE,
                        'relative flex w-full flex-row place-content-center place-items-center rounded-xs bg-primary duration-100 ease-linear ring-inset',

                        // Disabled state styles
                        isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
                        'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

                        context?.wrapperClassName,
                        wrapperClassName,
                    )
                }
            >
                {/* Leading icon */}
                {isValidElement(LeadingIcon) && LeadingIcon}
                {isReactComponent(LeadingIcon) && (
                    <LeadingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            isDisabled && 'text-fg-disabled',
                            styles.sizes[inputSize].iconLeading,
                        )}
                    />
                )}

                {/* Input field */}
                <AriaInput
                    {...(inputProps as AriaInputProps)}
                    ref={ref}
                    placeholder={placeholder}
                    className={cx(
                        'm-0 w-full rounded-xs bg-transparent text-primary outline-hidden placeholder:text-placeholder',
                        // autofill in chrome / firefox
                        'autofill:rounded-xs autofill:text-primary autofill:bg-transparent',
                        // disabled state
                        isDisabled && 'cursor-not-allowed text-disabled overflow-ellipsis',
                        'group-disabled:cursor-not-allowed group-disabled:text-disabled group-disabled:overflow-ellipsis',
                        // ring styles
                        inputRingStyles,
                        // input size styles
                        styles.sizes[inputSize].input,
                        // padding adjustments when leading/trailing icons are present
                        hasLeadingIcon && styles.sizes[inputSize].input_withLeadingIcon,
                        hasTrailingIcon && styles.sizes[inputSize].input_withTrailingIcon,
                        // context and prop class names
                        context?.inputClassName,
                        inputClassName,
                    )}
                />

                {/* Trailing icon - takes precedence over tooltip */}
                {isValidElement(TrailingIcon) && TrailingIcon}
                {isReactComponent(TrailingIcon) && (
                    <TrailingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            isDisabled && 'text-fg-disabled',
                            styles.sizes[inputSize].iconTrailing,
                        )}
                    />
                )}

                {/* Tooltip and help icon - only shown if no trailing icon is provided */}
                {tooltip && !TrailingIcon && (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <TooltipTrigger
                            className={cx(
                                'absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
                                styles.sizes[inputSize].iconTrailing,
                                context?.tooltipClassName,
                                tooltipClassName,
                            )}
                        >
                            <HelpCircle className="size-4" />
                        </TooltipTrigger>
                    </Tooltip>
                )}
            </AriaGroup>
        );
    },
);

InputBase.displayName = SEMANTIC_NAME_BASE;

interface BaseProps {
    /** Label text for the input */
    label?: string;
    /** Helper text displayed below the input */
    hint?: ReactNode;
}

export interface TextFieldProps
    extends BaseProps,
        AriaTextFieldProps,
        Pick<InputBaseProps, 'size' | 'wrapperClassName' | 'inputClassName' | 'tooltipClassName'> {
    ref?: Ref<HTMLDivElement>;
}

export const TextField = ({ className, ...props }: TextFieldProps) => {
    return (
        <TextFieldContext.Provider value={props}>
            <AriaTextField
                {...props}
                data-input-wrapper
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_TEXT_FIELD,
                        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            />
        </TextFieldContext.Provider>
    );
};

TextField.displayName = SEMANTIC_NAME_TEXT_FIELD;

export interface InputProps extends InputBaseProps, BaseProps {
    /** Whether to hide required indicator from label */
    hideRequiredIndicator?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            size = 'sm',
            placeholder,
            iconLeading,
            iconTrailing,
            label,
            hint,
            hideRequiredIndicator,
            className,
            groupRef,
            tooltip,
            inputClassName,
            wrapperClassName,
            tooltipClassName,
            ...props
        },
        ref,
    ) => {
        return (
            <TextField aria-label={!label ? placeholder : undefined} {...props} className={className}>
                {({ isRequired, isInvalid }) => (
                    <>
                        {label && (
                            <Label size={size} isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}>
                                {label}
                            </Label>
                        )}

                        <InputBase
                            {...{
                                ref,
                                groupRef,
                                size,
                                placeholder,
                                iconLeading,
                                iconTrailing,
                                inputClassName,
                                wrapperClassName,
                                tooltipClassName,
                                tooltip,
                            }}
                        />

                        {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
                    </>
                )}
            </TextField>
        );
    },
);

Input.displayName = SEMANTIC_NAME;
